import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  TrendingUp, Target, CheckCircle2, Clock, AlertCircle,
  Loader2, ChevronDown, ChevronUp, Activity, Ruler, Plus, Edit2
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";
import api from "../../lib/api";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ChartTooltip,
  Legend
} from "recharts";

// ─── helpers ────────────────────────────────────────────────────────────────
const GOAL_TYPE_COLORS = {
  weight_loss: { bg: "bg-blue-500/10", text: "text-blue-500" },
  muscle_gain: { bg: "bg-purple-500/10", text: "text-purple-500" },
  endurance: { bg: "bg-orange-500/10", text: "text-orange-500" },
  flexibility: { bg: "bg-pink-500/10", text: "text-pink-500" },
  general_fitness: { bg: "bg-teal-500/10", text: "text-teal-500" },
  default: { bg: "bg-primary/10", text: "text-primary" },
};

const MEASUREMENT_INFO = {
  weight: { label: "Weight", unit: "kg" },
  chest: { label: "Chest", unit: "cm" },
  waist: { label: "Waist", unit: "cm" },
  arm: { label: "Arm", unit: "cm" },
  bmi: { label: "BMI", unit: "" },
};

function goalColor(type) {
  return GOAL_TYPE_COLORS[type] || GOAL_TYPE_COLORS.default;
}

function ProgressBar({ value, max = 100, color = "bg-primary" }) {
  const pct = Math.min(Math.max((value / Math.max(max, 1)) * 100, 0), 100);
  return (
    <div className="h-2 bg-secondary rounded-full overflow-hidden">
      <div className={`h-full rounded-full transition-all duration-500 ${color}`} style={{ width: `${pct}%` }} />
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color = "text-primary" }) {
  return (
    <Card className="bg-card border-border">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-1">{label}</p>
            <p className={`text-2xl font-black font-heading ${color}`}>{value}</p>
          </div>
          <div className={`w-9 h-9  flex items-center justify-center shrink-0 ${color}`}>
            <Icon className="h-4 w-4" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Mini inline sparkline (SVG) for measurement trend
function Sparkline({ values, color = "#6366f1" }) {
  if (!values || values.length < 2) return null;
  const W = 80, H = 28, pad = 4;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const pts = values.map((v, i) => {
    const x = pad + (i / (values.length - 1)) * (W - pad * 2);
    const y = H - pad - ((v - min) / range) * (H - pad * 2);
    return `${x},${y}`;
  });
  return (
    <svg width={W} height={H} className="shrink-0">
      <polyline points={pts.join(" ")} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" />
      <circle cx={pts[pts.length - 1].split(",")[0]} cy={pts[pts.length - 1].split(",")[1]} r="2.5" fill={color} />
    </svg>
  );
}

// ─── GoalCard ────────────────────────────────────────────────────────────────
function GoalCard({ goal, onRefresh }) {
  const [open, setOpen] = useState(false);
  const [logOpen, setLogOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [expandedTab, setExpandedTab] = useState("list"); // list | graph
  const c = goalColor(goal.goalType);
  const pct = Math.min(Math.max(goal.progressPercent || 0, 0), 100);
  const isActive = goal.status === "Active";
  const entries = goal.entries || [];

  const chartData = useMemo(() => {
    if (!entries.length) return [];
    return [...entries]
      .sort((a, b) => {
        const da = a.recordedDate || a.date || a.createdAt || "";
        const db = b.recordedDate || b.date || b.createdAt || "";
        return da.localeCompare(db);
      })
      .map(e => {
        const rawDate = e.recordedDate || e.date || e.createdAt || "";
        let formattedDate = "";
        try {
          formattedDate = rawDate ? new Date(rawDate).toLocaleDateString("en-IN", { day: "numeric", month: "short" }) : "";
        } catch {
          formattedDate = rawDate ? rawDate.slice(5, 10) : "";
        }
        const dp = { date: formattedDate };
        (e.measurements || []).forEach(m => {
          dp[m.metric] = m.value;
        });
        return dp;
      });
  }, [entries]);

  const metrics = useMemo(() => {
    return goal.measurements?.map(m => m.metric) || [];
  }, [goal.measurements]);

  // group entries by metric for sparklines
  const byMetric = {};
  entries.forEach(e => {
    (e.measurements || []).forEach(m => {
      if (!byMetric[m.metric]) byMetric[m.metric] = [];
      byMetric[m.metric].push(m.value);
    });
  });

  return (
    <>
      <Card className={`bg-card border-border ${isActive ? "ring-1 ring-primary/30" : ""}`}>
        <CardContent className="p-4 space-y-3">
          {/* Title row */}
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="font-semibold text-sm truncate">{goal.name}</p>
              <span className={`inline-block text-[10px] font-medium px-1.5 py-0.5 rounded-full mt-1 ${c.bg} ${c.text}`}>
                {(goal.goalType || "goal").replace(/_/g, " ")}
              </span>
            </div>
            <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full shrink-0 ${isActive ? "bg-green-500/10 text-green-500" : "bg-secondary text-muted-foreground"
              }`}>
              {isActive ? <CheckCircle2 className="h-3 w-3" /> : <Clock className="h-3 w-3" />}
              {goal.status}
            </span>
          </div>

          {/* Progress bar */}
          <div className="space-y-1">
            <div className="flex justify-between items-center text-xs text-muted-foreground mb-1">
              <span>Progress ({pct}%)</span>
              {isActive && goal.allowMemberToRecord && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 px-2.5 text-[11px] font-heading uppercase tracking-wide rounded-lg flex items-center gap-1 shrink-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedEntry(null);
                    setLogOpen(true);
                  }}
                >
                  <Plus className="h-3.5 w-3.5" /> Log Progress
                </Button>
              )}
            </div>
            <ProgressBar value={pct} max={100} color="bg-primary" />
          </div>

          {/* Dates */}
          {(goal.startDate || goal.targetDate) && (
            <div className="flex gap-3 text-xs text-muted-foreground">
              {goal.startDate && (
                <span>Start: <span className="text-foreground font-medium">
                  {new Date(goal.startDate).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                </span></span>
              )}
              {goal.targetDate && (
                <span>Target: <span className="text-foreground font-medium">
                  {new Date(goal.targetDate).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                </span></span>
              )}
            </div>
          )}

          {/* Tracked Measurements */}
          {goal.measurements?.length > 0 && (
            <div className="space-y-2">
              {goal.measurements.map((m) => {
                const info = MEASUREMENT_INFO[m.metric] || { label: m.metric, unit: "" };
                // Find all values for this metric to build sparkline values
                const vals = [m.currentValue]; // Start with the initial current value
                entries.forEach(e => {
                  const pm = e.measurements?.find(p => p.metric === m.metric);
                  if (pm) vals.push(pm.value);
                });

                const latestValue = vals[vals.length - 1];
                const isImproved = m.targetValue < m.currentValue 
                  ? latestValue < m.currentValue // For weight loss: lower is better
                  : latestValue > m.currentValue; // For others: higher is better
                
                const diff = latestValue - m.currentValue;

                return (
                  <div key={m.metric} className="flex items-center justify-between gap-3 p-2 bg-secondary/10 rounded-lg border border-border/30">
                    <div className="min-w-0">
                      <p className="text-[10px] font-mono uppercase text-muted-foreground truncate">{info.label}</p>
                      <p className="text-xs font-semibold flex items-baseline gap-1.5">
                        <span className="font-mono">{latestValue}</span>
                        {info.unit && <span className="text-[10px] text-muted-foreground normal-case">{info.unit}</span>}
                        <span className="text-[10px] text-muted-foreground">/ Target: {m.targetValue}</span>
                        {vals.length > 1 && diff !== 0 && (
                          <span className={`text-[10px] font-medium ${isImproved ? "text-green-500" : "text-red-400"}`}>
                            {diff < 0 ? "▼" : "▲"}{Math.abs(diff).toFixed(1)}
                          </span>
                        )}
                      </p>
                    </div>
                    {vals.length > 1 && <Sparkline values={vals} color="#6366f1" />}
                  </div>
                );
              })}
            </div>
          )}

          {/* History toggle */}
          {entries.length > 0 && (
            <div>
              <button
                onClick={() => setOpen(!open)}
                className="flex items-center gap-1.5 text-xs font-medium text-primary hover:underline"
              >
                <Activity className="h-3.5 w-3.5" />
                {open ? "Hide" : "Show"} {entries.length} entr{entries.length > 1 ? "ies" : "y"}
                {open ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
              </button>
              {open && (
                <div className="mt-3 space-y-3">
                  {/* View switcher */}
                  <div className="flex border border-border rounded-lg overflow-hidden max-w-[200px] text-xs">
                    <button
                      onClick={() => setExpandedTab("list")}
                      className={`flex-1 py-1 text-[11px] font-medium transition-colors ${expandedTab === "list"
                          ? "bg-primary text-primary-foreground"
                          : "bg-secondary text-muted-foreground hover:text-foreground"
                        }`}
                    >
                      List
                    </button>
                    <button
                      onClick={() => setExpandedTab("graph")}
                      className={`flex-1 py-1 text-[11px] font-medium transition-colors ${expandedTab === "graph"
                          ? "bg-primary text-primary-foreground"
                          : "bg-secondary text-muted-foreground hover:text-foreground"
                        }`}
                    >
                      Graph
                    </button>
                  </div>

                  {expandedTab === "list" ? (
                    <div className="border border-border rounded-lg overflow-hidden">
                      {entries.map((e, i) => (
                        <div key={i} className="px-3 py-2.5 border-b border-border last:border-0 bg-secondary/30">
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="text-xs font-medium">
                                {e.recordedDate
                                  ? new Date(e.recordedDate).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })
                                  : "—"}
                              </p>
                              {e.creatorName && (
                                <span className="text-[10px] text-muted-foreground font-medium">by {e.creatorName}</span>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              {isActive && goal.allowMemberToRecord && e.createdBy && e.createdBy.startsWith("member:") && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 px-1.5 text-[10px] font-semibold text-primary hover:underline uppercase tracking-wide flex items-center gap-1"
                                  onClick={(evt) => {
                                    evt.stopPropagation();
                                    setSelectedEntry(e);
                                    setLogOpen(true);
                                  }}
                                >
                                  <Edit2 className="h-3 w-3" /> Edit
                                </Button>
                              )}
                              {e.notes && <p className="text-[10px] text-muted-foreground italic truncate max-w-[120px]">{e.notes}</p>}
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-x-4 gap-y-0.5">
                            {(e.measurements || []).map((m, mi) => (
                              <span key={mi} className="text-[10px] text-muted-foreground">
                                <span className="font-mono uppercase">{m.metric.replace(/_/g, " ")}</span>: {m.value}
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="h-[250px] p-3 border border-border rounded-lg bg-secondary/10" data-testid="member-progress-graph-container">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={chartData} margin={{ top: 5, right: 20, left: -20, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" className="stroke-border/40" />
                          <XAxis dataKey="date" className="text-[9px] font-mono fill-muted-foreground" />
                          <YAxis className="text-[9px] font-mono fill-muted-foreground" />
                          <ChartTooltip
                            contentStyle={{ backgroundColor: "hsl(var(--card))", borderColor: "hsl(var(--border))", borderRadius: "8px" }}
                            labelStyle={{ color: "hsl(var(--muted-foreground))", fontSize: "10px", fontWeight: "bold" }}
                            itemStyle={{ fontSize: "11px" }}
                          />
                          <Legend className="text-[9px] font-mono uppercase mt-1" />
                          {metrics.map((metric, idx) => {
                            const colors = ["#2563eb", "#10b981", "#d97706", "#db2777", "#7c3aed"];
                            return (
                              <Line
                                key={metric}
                                type="monotone"
                                dataKey={metric}
                                name={metric.charAt(0).toUpperCase() + metric.slice(1)}
                                stroke={colors[idx % colors.length]}
                                activeDot={{ r: 5 }}
                                strokeWidth={1.5}
                              />
                            );
                          })}
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {isActive && goal.allowMemberToRecord && (
        <AddMemberProgressModal
          open={logOpen}
          onOpenChange={setLogOpen}
          goal={goal}
          existingEntry={selectedEntry}
          onSuccess={onRefresh}
        />
      )}
    </>
  );
}


function AddMemberProgressModal({ open, onOpenChange, goal, onSuccess, existingEntry = null }) {
  const [loading, setLoading] = useState(false);
  const [values, setValues] = useState({});
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (goal?.measurements) {
      const initial = {};
      if (existingEntry) {
        (existingEntry.measurements || []).forEach(m => {
          initial[m.metric] = m.value;
        });
        setNotes(existingEntry.notes || "");
      } else {
        goal.measurements.forEach(m => {
          let latestVal = m.currentValue;
          if (goal.entries?.length > 0) {
            const lastEntry = goal.entries[0];
            const lastM = lastEntry.measurements?.find(pm => pm.metric === m.metric);
            if (lastM) latestVal = lastM.value;
          }
          initial[m.metric] = latestVal;
        });
        setNotes("");
      }
      setValues(initial);
    }
  }, [goal, open, existingEntry]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const token = localStorage.getItem("member_token");
      const payload = {
        goalId: goal.id,
        notes,
        measurements: Object.entries(values).map(([metric, value]) => ({
          metric,
          value: parseFloat(value) || 0,
        })),
      };
      if (existingEntry) {
        await api.put(`/member-portal/progress/${existingEntry.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Progress updated successfully!");
      } else {
        await api.post("/member-portal/progress", payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Progress logged successfully!");
      }
      onSuccess();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save progress");
    } finally {
      setLoading(false);
    }
  };

  if (!goal) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase tracking-wide flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-primary" />
            {existingEntry ? "Edit Progress" : "Log Progress"}
          </DialogTitle>
          <DialogDescription>
            {existingEntry
              ? `Edit your measurements for goal: ${goal.name}`
              : `Enter your current measurements for goal: ${goal.name}`}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 py-2">
          {goal.measurements?.map(m => {
            const label = m.metric.charAt(0).toUpperCase() + m.metric.slice(1);
            return (
              <div key={m.metric} className="space-y-2">
                <Label className="text-xs font-mono uppercase tracking-wide text-muted-foreground flex justify-between">
                  <span>{label}</span>
                  <span className="text-[10px] font-sans normal-case">Target: {m.targetValue}</span>
                </Label>
                <Input
                  type="number"
                  step="0.1"
                  value={values[m.metric] ?? ""}
                  onChange={(e) => setValues({ ...values, [m.metric]: e.target.value })}
                  placeholder={`Enter current ${m.metric}...`}
                  required
                />
              </div>
            );
          })}

          <div className="space-y-2">
            <Label className="text-xs font-mono uppercase tracking-wide text-muted-foreground">Notes (Optional)</Label>
            <Textarea
              placeholder="How are you feeling today? Any details to add..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="min-h-[80px]"
            />
          </div>

          <Button type="submit" className="w-full h-11 font-heading uppercase tracking-wider" disabled={loading}>
            {loading ? "Saving..." : existingEntry ? "Update Progress" : "Save Progress"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ─── Main page ─────────────────────────────────────────────────────────────────
export default function MemberProgress() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("active"); // active | all
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  useEffect(() => {
    (async () => {
      const token = localStorage.getItem("member_token");
      if (!token) { navigate("/"); return; }
      try {
        const res = await api.get("/member-portal/progress", {
          headers: { Authorization: `Bearer ${token}` },
        });
        setData(res.data);
      } catch (err) {
        if (err?.response?.status === 401) { navigate("/"); return; }
        setError("Failed to load progress.");
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate, refreshTrigger]);

  if (loading) return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  );
  if (error) return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] gap-3 text-muted-foreground">
      <AlertCircle className="h-8 w-8" /><p>{error}</p>
    </div>
  );

  const goals = data?.goals || [];
  const active = goals.filter(g => g.status === "Active");
  const completed = goals.filter(g => g.status !== "Active");
  const totalEntries = goals.reduce((s, g) => s + (g.entries?.length || 0), 0);

  const displayed = tab === "active" ? active : goals;
  const noGoals = goals.length === 0;

  return (
    <div className="flex flex-col gap-4 pb-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-black font-heading uppercase tracking-wide">Progress</h1>
        <p className="text-muted-foreground text-sm mt-0.5">Your fitness goals and measurement history</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <StatCard icon={Target} label="Active Goals" value={active.length} color="text-green-500" />
        <StatCard icon={CheckCircle2} label="Completed" value={completed.length} color="text-primary" />
        <StatCard icon={Activity} label="Total Entries" value={totalEntries} color="text-amber-400" />
      </div>

      {noGoals ? (
        <Card className="bg-card border-border">
          <CardContent className="flex flex-col items-center justify-center py-14 gap-3 text-muted-foreground">
            <TrendingUp className="h-10 w-10 opacity-30" />
            <p className="text-sm">No goals found</p>
            <p className="text-xs text-center max-w-xs">Contact the gym staff to set up your fitness goals and start tracking your progress.</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Tabs */}
          <div className="flex w-full rounded-xl overflow-hidden border border-border">
            {[
              { key: "active", label: `Active (${active.length})`, icon: Target },
              { key: "all", label: `All Goals (${goals.length})`, icon: TrendingUp },
            ].map(({ key, label, icon: Icon }, i, arr) => (
              <button
                key={key}
                onClick={() => setTab(key)}
                className={[
                  "flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-medium transition-colors",
                  i < arr.length - 1 ? "border-r border-border" : "",
                  tab === key
                    ? "bg-primary text-primary-foreground"
                    : "bg-secondary text-muted-foreground hover:text-foreground",
                ].join(" ")}
              >
                <Icon className="h-4 w-4 shrink-0" />{label}
              </button>
            ))}
          </div>

          {displayed.length === 0 ? (
            <Card className="bg-card border-border">
              <CardContent className="flex flex-col items-center justify-center py-12 gap-2 text-muted-foreground">
                <Ruler className="h-8 w-8 opacity-30" />
                <p className="text-sm">{tab === "active" ? "No active goals" : "No goals found"}</p>
              </CardContent>
            </Card>
          ) : (
            <div className="flex flex-col gap-4">
              {displayed.map((g, i) => <GoalCard key={i} goal={g} onRefresh={() => setRefreshTrigger(prev => prev + 1)} />)}
            </div>
          )}
        </>
      )}
    </div>
  );
}
